import { SOURCE_LABELS, type Provenance } from '@ops/contracts'

import { cx } from '../lib/cx'
import { formatRelativeDays } from '../lib/format'
import { Icon, type IconName } from './Icon'

/**
 * Proveniencia de un número: de dónde salió, hace cuánto y quién lo cargó.
 * Va debajo de **cada** cifra del panel, sin excepción: un número sin fuente es
 * un número que nadie puede defender en una reunión.
 *
 * Cinco variantes, y las cinco dicen su estado **en palabras**: el color nunca
 * es la única señal.
 *
 *  - **automática** — `stripe · sync hoy 06:00`
 *  - **manual** — `manual · hace 3 días · Lucía Fernández` (borde punteado)
 *  - **atención** — agrega `· por vencer` (8 a 30 días sin actualizar). Es el
 *    estado del medio de `freshness`: si no se dibuja, un dato de hace 12 días
 *    se lee igual que uno de hoy y nadie descubre que entró en zona de riesgo
 *    hasta que ya venció.
 *  - **vencida** — agrega `· vencido` y pasa a ámbar (>30 días sin actualizar)
 *  - **sin declarar** — `fuente sin declarar · cargalo en Admin`, cuando
 *    `provenance` es `null`. No se le inventa una fuente a un dato que no la
 *    tiene declarada.
 */
export type ProvenanceVariante =
  | 'automatica'
  | 'manual'
  | 'atencion'
  | 'vencida'
  | 'sin-declarar'

export interface ProvenanceBadgeProps {
  /** `null` = la fuente no está configurada → variante "sin declarar". */
  provenance: Provenance | null
  /** Inyectable, para tests y para compartir un mismo "ahora" en la pantalla. */
  now?: Date
  /** Ruta interna donde se declara la fuente. Sin esto el texto no linkea. */
  hrefAdmin?: string
  /** `inline` quita la caja: para dentro de una celda de tabla de 32px. */
  variant?: 'chip' | 'inline'
  className?: string
}

/**
 * Tres escalones visibles de frescura, y cada uno cambia más que el color:
 * fresco va gris sobre fondo neutro, "por vencer" sube a ámbar legible con el
 * borde sólido y el reloj, y vencido además se rellena.
 */
const TONO: Record<ProvenanceVariante, string> = {
  automatica: 'border-border bg-n100 text-muted-strong',
  manual: 'border-dashed border-n300 bg-surface text-muted-strong',
  atencion: 'border-warning bg-surface text-warning-text',
  vencida: 'border-warning bg-warning-soft text-warning-text',
  'sin-declarar': 'border-dashed border-warning bg-warning-soft text-warning-text',
}

const ICONO: Record<ProvenanceVariante, IconName> = {
  automatica: 'sincronizar',
  manual: 'lapiz',
  atencion: 'reloj',
  vencida: 'atencion',
  'sin-declarar': 'atencion',
}

/** Las variantes que se leen en ámbar también en la versión sin caja. */
const VARIANTES_AMBAR: ReadonlySet<ProvenanceVariante> = new Set([
  'atencion',
  'vencida',
  'sin-declarar',
])

/**
 * Hora del sync, para que un dato de hoy diga a qué hora corrió.
 *
 * La zona está fijada en Buenos Aires y no se lee del navegador a propósito: el
 * equipo trabaja en una sola zona y el panel es la referencia común. Si el
 * horario dependiera de la máquina, dos personas mirando el mismo dato verían
 * horas distintas y ninguna sabría cuál es la del sync.
 */
const ZONA = 'America/Argentina/Buenos_Aires'

function hora(iso: string): string | null {
  const fecha = new Date(iso)
  if (Number.isNaN(fecha.getTime())) return null
  return new Intl.DateTimeFormat('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ZONA,
  }).format(fecha)
}

export function varianteDe(
  provenance: Provenance | null,
): ProvenanceVariante {
  if (provenance === null) return 'sin-declarar'
  if (provenance.freshness === 'expired') return 'vencida'
  if (provenance.freshness === 'stale') return 'atencion'
  return provenance.source === 'manual' ? 'manual' : 'automatica'
}

export function ProvenanceBadge({
  provenance,
  now = new Date(),
  hrefAdmin,
  variant = 'chip',
  className,
}: ProvenanceBadgeProps) {
  const variante = varianteDe(provenance)

  const contenido =
    provenance === null ? (
      <>
        fuente sin declarar ·{' '}
        {hrefAdmin !== undefined ? (
          <a
            href={hrefAdmin}
            className="font-semibold underline underline-offset-2"
          >
            cargalo en Admin
          </a>
        ) : (
          <span className="font-semibold">cargalo en Admin</span>
        )}
      </>
    ) : (
      textoDe(provenance, now)
    )

  return (
    <span
      data-testid="provenance-badge"
      data-variante={variante}
      data-freshness={provenance?.freshness ?? 'sin-declarar'}
      data-source={provenance?.source ?? 'sin-declarar'}
      className={cx(
        'inline-flex max-w-full items-center gap-1 text-micro font-medium leading-4',
        variant === 'chip'
          ? cx('rounded border px-1.5 py-0.5', TONO[variante])
          : cx(
              'border-0 bg-transparent p-0',
              VARIANTES_AMBAR.has(variante)
                ? 'text-warning-text'
                : 'text-muted-strong',
            ),
        className,
      )}
    >
      <Icon name={ICONO[variante]} size={10} />
      <span className="min-w-0 truncate">{contenido}</span>
    </span>
  )
}

function textoDe(provenance: Provenance, now: Date): string {
  const { source, updatedAt, updatedBy, freshness } = provenance
  const esAdapter = source !== 'manual'
  const relativo = formatRelativeDays(updatedAt, now)

  // "sync hoy" solo tiene sentido si alguna vez sincronizó. Si nunca hubo dato,
  // el badge dice "nunca" y nada más.
  let tiempo = relativo
  if (updatedAt !== null && esAdapter) {
    const h = relativo === 'hoy' || relativo === 'ayer' ? hora(updatedAt) : null
    tiempo = h === null ? `sync ${relativo}` : `sync ${relativo} ${h}`
  }

  const partes = [SOURCE_LABELS[source], tiempo]
  if (updatedBy !== null) partes.push(updatedBy)
  // La frescura se dice, no se pinta: sin la palabra, "por vencer" y "fresco"
  // son el mismo badge para quien no distingue el ámbar del gris.
  if (freshness === 'stale') partes.push('por vencer')
  if (freshness === 'expired') partes.push('vencido')

  return partes.join(' · ')
}
